import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, CardBody, Alert, Table, Spinner } from "reactstrap";
import { Formik, ErrorMessage } from 'formik';
import moment from 'moment';
import CustomDatePicker from '../../components/Common/dateRangeDatepicker';
import { errorMessage } from 'constants/errorMessages';
import StatisticsService from '../../services/StatisticsService';
import PaginationTableFooter from '../patientManagement/tableComponents/paginationFooter';
import { YYYY_MM_DD } from '../../constants/dateFormats';

export default function Statistics() {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentOffset, setCurrentOffset] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const limit = 100;

    const initialValues = {
        startDate: moment().subtract(6, 'months').toDate(),
        endDate: new Date()
    };

    const [currentFilters, setCurrentFilters] = useState({
        startDate: moment(initialValues.startDate).format(YYYY_MM_DD),
        endDate: moment(initialValues.endDate).format(YYYY_MM_DD)
    });

    const loadStatistics = useCallback(
        async () => {
            setLoading(true);
            setError(null);
            
            try {
                const result = await StatisticsService.getUsersWithGclid({
                    ...currentFilters,
                    page: currentPage,
                    limit
                });
                setData(result.data);
            } catch (err) {
                setError('Failed to load data. Please try again.');
            } finally {
                setLoading(false);
            }
        },
        [currentFilters, currentPage, limit]
    );

    useEffect(() => {
        loadStatistics();
    }, [loadStatistics]);

    const handleSubmit = useCallback((values) => {
        const apiFilters = {
            startDate: moment(values.startDate).format(YYYY_MM_DD),
            endDate: moment(values.endDate).format(YYYY_MM_DD)
        };
        setCurrentFilters(apiFilters);
        setCurrentPage(1);
        setCurrentOffset(0);
    }, []);

    const handlePageChange = useCallback(({ selected }) => {
        const offset = selected * limit;
        setCurrentOffset(offset);
        setCurrentPage(selected + 1);
    }, [limit]);

    return (
        <div className="page-content">
            <Container fluid>
                <Row className="mb-4">
                    <Col lg={12}>
                        <h4 className="page-title">Users with GCLID</h4>
                        <p className="text-muted">View users who came from advertising campaigns</p>
                    </Col>
                </Row>

                {error && (
                    <Row className="mb-4">
                        <Col lg={12}>
                            <Alert color="danger">
                                {error}
                            </Alert>
                        </Col>
                    </Row>
                )}

                <Row className="mb-4">
                    <Col lg={12}>
                        <Card>
                            <CardBody>
                                <Formik
                                    initialValues={initialValues}
                                    onSubmit={handleSubmit}
                                    enableReinitialize={false}
                                >
                                    {({
                                        values,
                                        errors,
                                        touched,
                                        setFieldValue,
                                        handleSubmit
                                    }) => (
                                        <form onSubmit={handleSubmit}>
                                            <Row className="align-items-end">
                                                <Col md={4}>
                                                    <CustomDatePicker
                                                        errors={errors}
                                                        touched={touched}
                                                        ComponentStart={() => (<ErrorMessage name="startDate" render={errorMessage}/>)}
                                                        ComponentEnd={() => (<ErrorMessage name="endDate" render={errorMessage}/>)}
                                                        setFieldValue={setFieldValue}
                                                        currentStartDay={values.startDate}
                                                        currentEndDay={values.endDate}
                                                        handleChange={() => {}}
                                                        isDisabled={loading}
                                                        minDate={null}
                                                        maxDate={new Date()}
                                                    />
                                                </Col>
                                                <Col md={2}>
                                                    <button 
                                                        type="submit"
                                                        className="btn btn-primary w-100"
                                                        disabled={loading}
                                                    >
                                                        Apply Filter
                                                    </button>
                                                </Col>
                                                {data && (
                                                    <Col md={3} className="text-end">
                                                        <div className="d-flex justify-content-end gap-4">
                                                            <h5 className="mb-0">
                                                                Total Users: <span className="text-primary">{data.totalCount}</span>
                                                            </h5>
                                                        </div>
                                                    </Col>
                                                )}
                                                {data && (
                                                    <Col md={3} className="text-end">
                                                        <div className="d-flex justify-content-end gap-4">
                                                            <h5 className="mb-0">
                                                                Tatal Paid Users: <span className="text-success">{data.totalPaid}</span>
                                                            </h5>
                                                        </div>
                                                    </Col>
                                                )}
                                            </Row>
                                        </form>
                                    )}
                                </Formik>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>

                {loading && !data && (
                    <Row>
                        <Col lg={12} className="text-center">
                            <Spinner color="primary" />
                            <p className="mt-2">Loading data...</p>
                        </Col>
                    </Row>
                )}

                {data && data.users && (
                    <Row>
                        <Col lg={12}>
                            <Card>
                                <CardBody>
                                    <div className="table-responsive">
                                        <Table className="table table-striped table-hover">
                                            <thead>
                                                <tr>
                                                    <th>ID</th>
                                                    <th>GCLID</th>
                                                    <th>Email</th>
                                                    <th>Paid</th>
                                                    <th>Created Date</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.users.length > 0 ? (
                                                    data.users.map((user) => (
                                                        <tr key={user.id}>
                                                            <td>{user.id}</td>
                                                            <td><code>{user.gclid}</code></td>
                                                            <td>{user.email}</td>
                                                            <td>
                                                                {user.isPaid ? (
                                                                    <span className="badge bg-success">Yes</span>
                                                                ) : (
                                                                    <span className="badge bg-secondary">No</span>
                                                                )}
                                                            </td>
                                                            <td>{moment(user.createdAt).format('DD/MM/YYYY HH:mm')}</td>
                                                        </tr>
                                                    ))
                                                ) : (
                                                    <tr>
                                                        <td colSpan="5" className="text-center text-muted">
                                                            No users found with GCLID in the selected date range
                                                        </td>
                                                    </tr>
                                                )}
                                            </tbody>
                                        </Table>
                                    </div>
                                    
                                    {data && data.totalCount > limit && (
                                        <div className="mt-5">
                                            <PaginationTableFooter 
                                                total={data.totalCount} 
                                                handleChangePage={handlePageChange} 
                                                currentOffset={currentOffset} 
                                                limit={limit} 
                                            />
                                        </div>
                                    )}
                                </CardBody>
                            </Card>
                        </Col>
                    </Row>
                )}
            </Container>
        </div>
    );
}
